import * as React from 'react'
import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import Loading from 'modules/core/components/Loading'

import { AutomationStep } from 'modules/automations/components/Show/Step'
import { automationSteps } from 'modules/automations/components/Show/Step/constants'
import {
  getActionName,
  getCustomField,
  isCustomField,
} from 'modules/automations/components/Show/actionUtils'
import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import type { Action } from 'modules/automations/store/models/Action'
import type {
  ITriggerCondition,
  ITriggerWithConditions,
} from 'modules/automations/store/models/Trigger'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import { Selects } from './Selects'

interface Props {
  form: Form | undefined
  condition: ITriggerCondition | undefined
  onClick?: () => void
  action: Action
  withLastSpacer?: boolean
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
  trigger?: ITriggerWithConditions
}

// TODO - epic-address
const HIDE_HAS_CHANGED_LIST_KEYS = ['active']

export const BranchAction = observer(
  ({
    form,
    condition,
    onClick,
    action,
    trigger,
    withLastSpacer,
    loadingCustomFields,
    customFieldsCollection,
  }: Props) => {
    const operation = useMemo<BranchOperation>(
      () => action.attributes.metadata.operations[0],
      [action]
    )

    const extensionFeatureHasConditions = () => {
      return trigger?.conditions?.length !== 0
    }

    /**
     * In default custom notifications, conditions cannot be interacted with in any way, that includes changing values in inputs and deleting conditions
     * @returns true if this is a default custom notification, false otherwise
     */
    const isDefault = (): boolean => {
      const automation = action.automation

      if (!automation) return true

      return automation.isDefault
    }

    /**
     * Controls wether the input fields are readonly or not, both be readonly for default custom notifications and in case of a missing custom field
     *
     * Unlike default custom notifications, if a custom field is missing, the user should still be able to remove
     * @returns true if inputs should be disabled
     */
    const isReadonly = (): boolean => {
      if (isCustomField(operation) && !getCustomField(operation, customFieldsCollection)) {
        return true
      }

      return isDefault()
    }

    /**
     * @returns this condition check if we have to hide has_changed operation option
     */
    const hideHasChanged = (): boolean => {
      if (extensionFeatureHasConditions()) {
        return !condition?.show_has_changed
      }

      return HIDE_HAS_CHANGED_LIST_KEYS.includes(operation.attribute)
    }

    if (isCustomField(operation) && loadingCustomFields) return <Loading />

    const conflictCondition = () => {
      return action?.automation?.attributes.conflict_keys.find(
        (item: { [key: string]: string }) => item.key === operation.attribute
      )
    }

    const title = () => {
      if (extensionFeatureHasConditions()) {
        const conditionLabel = condition?.label
        const conflictedCondition = conflictCondition()

        if (conflictedCondition) {
          return conflictedCondition.label
            ? conflictedCondition.label
            : getActionName(action, customFieldsCollection)
        }

        if (conditionLabel) {
          return conditionLabel
        }

        return getActionName(action, customFieldsCollection)
      }

      return getActionName(action, customFieldsCollection)
    }

    // We use title as boolean to wait to the loading of the header to show if really has conflict or not
    const hasConflict = () => extensionFeatureHasConditions() && !condition && !!title()

    const showElements = (): boolean => {
      return (
        !extensionFeatureHasConditions() || Boolean(extensionFeatureHasConditions() && !!title())
      )
    }

    return (
      <>
        {showElements() && (
          <AutomationStep
            action={action}
            disabled={isDefault()}
            onClick={onClick}
            stepType={automationSteps.condition}
            withLastSpacer={withLastSpacer}
            title={title()}
            conflict={hasConflict()}
          >
            <Box paddingX="s16" paddingBottom="s16">
              <FormComponent>
                {form && (
                  <Selects
                    disabled={isReadonly() || hasConflict()}
                    form={form}
                    operation={operation}
                    label={title()}
                    loadingCustomFields={loadingCustomFields}
                    customFieldsCollection={customFieldsCollection}
                    hideHasChanged={hideHasChanged()}
                    condition={condition}
                    extensionFeatureHasConditions={extensionFeatureHasConditions()}
                  />
                )}
              </FormComponent>
            </Box>
          </AutomationStep>
        )}
      </>
    )
  }
)
